import { action, comparer, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';

import { IApiClient } from '../../app/data/client';
import { GetDealerDocumentTermsQuery } from '../../app/data/model';
import type {
  GetTermsAndConditionsQueryResponse,
  PricingContext,
  TermsAndConditionsData,
  TermsAndConditionsParagraphData,
} from '../../app/data/model';
import { BasePageState } from '../../app/shared/BasePageState';
import { INavigationService } from '../../app/shared/NavigationService';
import { Tab } from '../../app/shared/Tabs/Tab';
import { Tabs } from '../../app/shared/Tabs/Tabs';
import { TermsQuery, TermsQueryParser } from './TermsQuery';

export interface BrandDocument {
  brandId: string;
  brandName: string;
  paragraphs: TermsAndConditionsParagraphData[];
}

export class TermsPageState extends BasePageState<null> {
  @observable.ref
  public brandTabs: Tabs;

  @observable.ref
  public documents: BrandDocument[] = [];

  private readonly urlQuery: TermsQuery;

  private disposeQueryReaction: IReactionDisposer;

  public constructor(
    private readonly client: IApiClient,
    private readonly navigation: INavigationService,
    private readonly pricingContext: PricingContext,
  ) {
    super();
    makeObservable(this);

    this.urlQuery = TermsQueryParser.toModel(navigation.currentUrl.query);
  }

  @computed
  public get activeDocument(): BrandDocument {
    const activeTabId = this.brandTabs?.activeTab?.id;
    return activeTabId && this.documents.find((document) => document.brandId === activeTabId);
  }

  @computed
  public get query(): Map<string, string> {
    return TermsQueryParser.toQuery({ brand: this.brandTabs?.activeTab?.id });
  }

  public async onLoad() {
    const response = await this.client.send(
      new GetDealerDocumentTermsQuery({
        pricingContext: this.pricingContext,
      }),
    );

    this.initialize(response);
  }

  public unload(): void {
    this.disposeQueryReaction?.();
  }

  @action
  private initialize(response: GetTermsAndConditionsQueryResponse) {
    const documentsByBrand = response.documents.groupBy<TermsAndConditionsData>((d) => d.brandId);

    this.documents = Object.entries(documentsByBrand).map(([brandId, documents]) => ({
      brandId,
      brandName: documents.first().brandName,
      paragraphs: documents.flatMap((document) => document.paragraphs),
    }));

    const activeBrandId =
      this.documents.find((document) => document.brandId === this.urlQuery.brand)?.brandId ??
      this.documents.first()?.brandId;

    this.brandTabs = new Tabs(
      this.documents.map(
        (document) => new Tab(document.brandId, document.brandName, document.brandId === activeBrandId),
      ),
    );

    this.disposeQueryReaction = reaction(
      () => this.query,
      () => this.navigation.setQuery(this.query),
      { equals: comparer.structural, fireImmediately: true },
    );
  }
}
